import React from 'react';
import { useMsal } from '@azure/msal-react';
import { acquireApiAccessTokenAsync } from '../../../services/auth/AuthService';
import { useProviderServices } from '../../../app/providers/AppServicesContext';
import { updateChatSessionHandler } from '../../../application/chat-sessions/handlers/UpdateChatSessionHandler';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { upsertSession } from '../../../redux/features/chatSessions/chatSessionsSlice';
import {
    removeSavedPersona,
    upsertSavedPersona,
} from '../../../redux/features/chatPersonalization/chatPersonalizationSlice';
import { alerts } from '../../../shared/ui/Alerts';
import { selectSelectedSession } from '../../../domain/selectors/selected/chat/selectSelectedSession';
import { selectSavedPersonas } from '../../../domain/selectors/base/chatPersonalization/selectSavedPersonas';
import { SavedPersona } from '../../../domain/chat/models/SavedPersona';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';

export type PersonaViewMode = 'editor' | 'library';

export interface UsePersonaVm {
    viewMode: PersonaViewMode;
    savedPersonas: SavedPersona[];
    isPersonaDialogOpen: boolean;
    personaValue: string;
    shouldSavePersona: boolean;
    personaTitle: string;
    editingSavedPersonaId: string | null;
    isPersonaSaving: boolean;
    personaPendingDelete: SavedPersona | null;
    isDeletePersonaDialogOpen: boolean;
    openPersonaDialog: () => void;
    closePersonaDialog: () => void;
    openPersonaLibrary: () => void;
    closePersonaLibrary: () => void;
    selectLibraryPersona: (persona: SavedPersona) => void;
    editLibraryPersona: (persona: SavedPersona) => void;
    openDeletePersonaDialog: (persona: SavedPersona) => void;
    closeDeletePersonaDialog: () => void;
    setPersonaValue: (value: string) => void;
    setShouldSavePersona: (value: boolean) => void;
    setPersonaTitle: (value: string) => void;
    savePersona: () => Promise<void>;
    deletePersona: () => Promise<void>;
}

export const usePersona = (): UsePersonaVm => {
    const dispatch = useAppDispatch();
    const providerServices = useProviderServices();
    const { instance } = useMsal();
    const session = useAppSelector(selectSelectedSession);
    const savedPersonas = useAppSelector(selectSavedPersonas);

    const sortedSavedPersonas = React.useMemo(
        () => [...savedPersonas].sort((a, b) => b.createdDate - a.createdDate),
        [savedPersonas],
    );

    const [viewMode, setViewMode] = React.useState<PersonaViewMode>('editor');
    const [isPersonaDialogOpen, setIsPersonaDialogOpen] = React.useState(false);
    const [personaValue, setPersonaValueState] = React.useState('');
    const [shouldSavePersona, setShouldSavePersonaState] = React.useState(false);
    const [personaTitle, setPersonaTitleState] = React.useState('');
    const [editingSavedPersonaId, setEditingSavedPersonaId] = React.useState<string | null>(null);
    const [isPersonaSaving, setIsPersonaSaving] = React.useState(false);
    const [personaPendingDelete, setPersonaPendingDelete] = React.useState<SavedPersona | null>(null);
    const [isDeletePersonaDialogOpen, setIsDeletePersonaDialogOpen] = React.useState(false);

    const openPersonaDialog = React.useCallback((): void => {
        setViewMode('editor');
        setPersonaValueState(session?.providerType === ChatProviderType.ProGPT ? session.persona ?? '' : '');
        setShouldSavePersonaState(false);
        setPersonaTitleState('');
        setEditingSavedPersonaId(null);
        setIsPersonaDialogOpen(true);
    }, [session]);

    const closePersonaDialog = React.useCallback((): void => {
        if (isPersonaSaving) {
            return;
        }

        setIsPersonaDialogOpen(false);
        setViewMode('editor');
        setPersonaValueState('');
        setShouldSavePersonaState(false);
        setPersonaTitleState('');
        setEditingSavedPersonaId(null);
    }, [isPersonaSaving]);

    const openPersonaLibrary = React.useCallback((): void => {
        setViewMode('library');
    }, []);

    const closePersonaLibrary = React.useCallback((): void => {
        setViewMode('editor');
    }, []);

    const selectLibraryPersona = React.useCallback((persona: SavedPersona): void => {
        setPersonaValueState(persona.content);
        setShouldSavePersonaState(false);
        setPersonaTitleState('');
        setEditingSavedPersonaId(null);
        setViewMode('editor');
    }, []);

    const editLibraryPersona = React.useCallback((persona: SavedPersona): void => {
        setPersonaValueState(persona.content);
        setShouldSavePersonaState(true);
        setPersonaTitleState(persona.title);
        setEditingSavedPersonaId(persona.id);
        setViewMode('editor');
    }, []);

    const openDeletePersonaDialog = React.useCallback((persona: SavedPersona): void => {
        setPersonaPendingDelete(persona);
        setIsDeletePersonaDialogOpen(true);
    }, []);

    const closeDeletePersonaDialog = React.useCallback((): void => {
        if (isPersonaSaving) {
            return;
        }

        setIsDeletePersonaDialogOpen(false);
        setPersonaPendingDelete(null);
    }, [isPersonaSaving]);

    const setPersonaValue = React.useCallback((value: string): void => {
        setPersonaValueState(value);
    }, []);

    const setShouldSavePersona = React.useCallback((value: boolean): void => {
        setShouldSavePersonaState(value);
        if (!value) {
            setEditingSavedPersonaId(null);
        }
    }, []);

    const setPersonaTitle = React.useCallback((value: string): void => {
        setPersonaTitleState(value);
    }, []);

    const savePersona = React.useCallback(async (): Promise<void> => {
        if (!session) {
            alerts.error('Please select a supported chat before setting a persona.');
            return;
        }

        const trimmedPersonaValue = personaValue.trim();
        const trimmedPersonaTitle = personaTitle.trim();

        if (!trimmedPersonaValue) {
            alerts.error('Persona text cannot be empty.');
            return;
        }

        if (shouldSavePersona && !trimmedPersonaTitle) {
            alerts.error('Persona title cannot be empty when saving to your library.');
            return;
        }

        setIsPersonaSaving(true);

        try {
            const accessToken = await acquireApiAccessTokenAsync({
                msalInstance: instance,
            });

            if (shouldSavePersona) {
                const savedPersona = editingSavedPersonaId
                    ? await providerServices.proGPT.personalization.editPersonaAsync(
                          editingSavedPersonaId,
                          {
                              title: trimmedPersonaTitle,
                              content: trimmedPersonaValue,
                          },
                          accessToken,
                      )
                    : await providerServices.proGPT.personalization.createPersonaAsync(
                          {
                              title: trimmedPersonaTitle,
                              content: trimmedPersonaValue,
                          },
                          accessToken,
                      );

                dispatch(upsertSavedPersona(savedPersona));
            }

            const updatedSession = await updateChatSessionHandler({
                command: {
                    sessionId: session.id,
                    persona: trimmedPersonaValue,
                },
                session,
                accessToken,
                providerServices,
            });

            dispatch(upsertSession(updatedSession.session));
            alerts.success(
                shouldSavePersona
                    ? editingSavedPersonaId
                        ? 'Saved persona updated and applied successfully.'
                        : 'Persona saved and applied successfully.'
                    : 'Persona applied successfully.',
            );
            closePersonaDialog();
        } catch {
            alerts.error(
                shouldSavePersona
                    ? editingSavedPersonaId
                        ? 'Failed to update and apply saved persona.'
                        : 'Failed to save and apply persona.'
                    : 'Failed to apply persona.',
            );
        } finally {
            setIsPersonaSaving(false);
        }
    }, [
        closePersonaDialog,
        dispatch,
        editingSavedPersonaId,
        instance,
        personaTitle,
        personaValue,
        providerServices,
        session,
        shouldSavePersona,
    ]);

    const deletePersona = React.useCallback(async (): Promise<void> => {
        if (!personaPendingDelete) {
            return;
        }

        setIsPersonaSaving(true);

        try {
            const accessToken = await acquireApiAccessTokenAsync({
                msalInstance: instance,
            });

            await providerServices.proGPT.personalization.deletePersonaAsync(personaPendingDelete.id, accessToken);

            dispatch(removeSavedPersona(personaPendingDelete.id));
            alerts.success('Saved persona deleted successfully.');
            closeDeletePersonaDialog();
        } catch {
            alerts.error('Failed to delete saved persona.');
        } finally {
            setIsPersonaSaving(false);
        }
    }, [closeDeletePersonaDialog, dispatch, instance, personaPendingDelete, providerServices]);

    return {
        viewMode,
        savedPersonas: sortedSavedPersonas,
        isPersonaDialogOpen,
        personaValue,
        shouldSavePersona,
        personaTitle,
        editingSavedPersonaId,
        isPersonaSaving,
        personaPendingDelete,
        isDeletePersonaDialogOpen,
        openPersonaDialog,
        closePersonaDialog,
        openPersonaLibrary,
        closePersonaLibrary,
        selectLibraryPersona,
        editLibraryPersona,
        openDeletePersonaDialog,
        closeDeletePersonaDialog,
        setPersonaValue,
        setShouldSavePersona,
        setPersonaTitle,
        savePersona,
        deletePersona,
    };
};

export default usePersona;
